/**
 * useOrchestrator Hook
 *
 * React hook for executing actions with async/polling support and automatic toast notifications.
 * Auto-detects processor capabilities and provides comprehensive state tracking.
 * Integrates centralized toast feedback for all actions.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Action, ProcessorResult } from '@/business/types/action.types';
import { AsyncRequestStatus } from '@/business/types/async.types';
import {
  actionOrchestrator,
  OrchestratorExecuteOptions,
} from '@/business/orchestrator/ActionOrchestrator';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useToast } from './useToast';

/**
 * Helper to determine if a ResponseCode indicates success.
 * All API responses are HTTP 200, actual errors are in the response body.
 */
function isResponseSuccess(code: ResponseCode): boolean {
  return [
    ResponseCode.SUCCESS,
    ResponseCode.CREATED,
    ResponseCode.ACCEPTED,
    ResponseCode.NO_CONTENT,
  ].includes(code);
}

/**
 * Progress state with async status
 */
export interface ProgressState {
  /** Progress percentage (0-100) */
  progress: number;
  /** Progress message */
  message?: string;
  /** Current async request status */
  asyncStatus: AsyncRequestStatus;
}

/**
 * Hook state
 */
export interface OrchestratorState<TOutput> {
  /** Whether an action is currently executing */
  isLoading: boolean;
  /** Current async request status (null for sync execution) */
  asyncStatus: AsyncRequestStatus | null;
  /** Current progress state */
  progress: ProgressState | null;
  /** Result of the last action execution */
  result: ProcessorResult<TOutput> | null;
  /** Response code from last execution */
  code: ResponseCode | null;
  /** User-friendly message from last execution */
  message: string | null;
  /** Async request ID (null for sync execution) */
  requestId: string | null;
  /** Convenience flag for checking if last action succeeded */
  isSuccess: boolean;
  /** Convenience flag for checking if last action failed */
  isError: boolean;
  /** Whether this action supports async execution */
  supportsAsync: boolean;
}

/**
 * Hook return type
 */
export interface UseOrchestratorReturn<
  TInput,
  TOutput,
> extends OrchestratorState<TOutput> {
  /** Execute an action with the provided data */
  execute: (
    data: TInput,
    options?: Omit<OrchestratorExecuteOptions<TOutput>, 'onResult'>
  ) => Promise<ProcessorResult<TOutput>>;
  /** Reset the hook state */
  reset: () => void;
}

/**
 * Hook for executing actions (sync or async).
 *
 * This hook provides a unified API regardless of whether the processor
 * supports async execution. It automatically:
 * - Detects processor capabilities on mount
 * - Routes to sync or async execution
 * - Tracks appropriate state for each execution type
 * - Provides consistent API surface
 *
 * @template TInput The type of input data for the action
 * @template TOutput The type of output data from the action
 * @param action The action to execute
 * @returns Hook state and execution methods
 *
 * @example
 * ```typescript
 * function LoginForm() {
 *   const {
 *     execute,
 *     isLoading,
 *     asyncStatus,
 *     progress,
 *     result,
 *     supportsAsync
 *   } = useOrchestrator<LoginInput, LoginOutput>(Action.LOGIN);
 *
 *   const handleLogin = async () => {
 *     const result = await execute(
 *       { username, password, school },
 *       {
 *         timeout: 30000,
 *         onProgress: (p, msg) => console.log(`${p}%: ${msg}`)
 *       }
 *     );
 *
 *     if (result.status === ActionStatus.SUCCESS) {
 *       console.log('User:', result.data.user);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       {isLoading && (
 *         <div>
 *           {asyncStatus && <p>Status: {asyncStatus}</p>}
 *           <p>Progress: {progress?.progress}%</p>
 *         </div>
 *       )}
 *       <button onClick={handleLogin} disabled={isLoading}>Login</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useOrchestrator<TInput = unknown, TOutput = unknown>(
  action: Action
): UseOrchestratorReturn<TInput, TOutput> {
  const [isLoading, setIsLoading] = useState(false);
  const [asyncStatus, setAsyncStatus] = useState<AsyncRequestStatus | null>(
    null
  );
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [result, setResult] = useState<ProcessorResult<TOutput> | null>(null);
  const [code, setCode] = useState<ResponseCode | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [supportsAsync, setSupportsAsync] = useState(false);

  // Toast integration — use ref to avoid destabilizing execute's identity
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  /**
   * All actions now support async via ActionOrchestrator
   */
  useEffect(() => {
    setSupportsAsync(true); // All actions use async action manager
  }, [action]);

  /**
   * Execute the action
   */
  const execute = useCallback(
    async (
      data: TInput,
      options: Omit<OrchestratorExecuteOptions<TOutput>, 'onResult'> = {}
    ): Promise<ProcessorResult<TOutput>> => {
      setIsLoading(true);
      setProgress({ progress: 0, asyncStatus: AsyncRequestStatus.SUBMITTED });
      setResult(null);
      setCode(null);
      setMessage(null);
      setRequestId(null);

      // All actions use the async action manager
      setAsyncStatus(AsyncRequestStatus.SUBMITTED);

      // Execute with enhanced options (toast methods are passed to orchestrator)
      const enhancedOptions: OrchestratorExecuteOptions<TOutput> = {
        ...options,
        toastMethods: toastRef.current, // Pass toast methods to orchestrator for centralized handling
        onProgress: (progressValue, progressMessage) => {
          setProgress({
            progress: progressValue,
            message: progressMessage,
            asyncStatus: AsyncRequestStatus.IN_PROGRESS,
          });

          // Call user's onProgress if provided
          options.onProgress?.(progressValue, progressMessage);
        },
        onResult: (resultCode, resultMessage, _resultData) => {
          // Store code and message
          setCode(resultCode);
          setMessage(resultMessage);

          // Check if success based on ResponseCode enum (all responses are HTTP 200, errors are in body)
          const isSuccessCode = isResponseSuccess(resultCode);

          if (isSuccessCode) {
            setAsyncStatus(AsyncRequestStatus.COMPLETED);
            setProgress({
              progress: 100,
              message: 'Completed',
              asyncStatus: AsyncRequestStatus.COMPLETED,
            });
          } else {
            setAsyncStatus(AsyncRequestStatus.FAILED);
            setProgress({
              progress: 0,
              message: 'Failed',
              asyncStatus: AsyncRequestStatus.FAILED,
            });
          }
        },
      };

      const processorResult = await actionOrchestrator.execute<TInput, TOutput>(
        action,
        data,
        enhancedOptions
      );

      setResult(processorResult);

      setIsLoading(false);

      // Keep progress visible briefly before clearing
      setTimeout(() => {
        setProgress(null);
      }, 1000);

      return processorResult;
    },
    [action]
  );

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setIsLoading(false);
    setAsyncStatus(null);
    setProgress(null);
    setResult(null);
    setCode(null);
    setMessage(null);
    setRequestId(null);
  }, []);

  // Compute isSuccess/isError based on response code
  const isSuccess = code ? isResponseSuccess(code) : false;
  const isError = code ? !isResponseSuccess(code) : false;

  return {
    isLoading,
    asyncStatus,
    progress,
    result,
    code,
    message,
    requestId,
    isSuccess,
    isError,
    supportsAsync,
    execute,
    reset,
  };
}
